// EPIC-P05-07: User Badge List Component
// Shows users who earned a badge with revoke and export functionality

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ResponsiveTable, ResponsiveTableColumn } from '../ResponsiveTable'
import { Button } from '@/components/ui/button'
import { AdminCard } from '../primitives'
import { getBadgeUsers, revokeBadge } from '@/lib/api/admin/badges'
import { Trash2, Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface UserBadgeListProps {
  badgeId: string
}

interface UserBadge {
  id: string
  user_id: string
  unlocked_at: string
  profiles: {
    id: string
    email: string
    full_name: string | null
  } | null
}

export function UserBadgeList({ badgeId }: UserBadgeListProps) {
  const [page, setPage] = useState(1)
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-badge-users', badgeId, page],
    queryFn: () => getBadgeUsers(badgeId, { page, limit: 50 }),
    enabled: !!badgeId,
  })

  const handleRevoke = async (userBadgeId: string) => {
    setRevokingId(userBadgeId)
    try {
      await revokeBadge(userBadgeId)
      toast.success('Badge revoked successfully')
      refetch()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to revoke badge')
    } finally {
      setRevokingId(null)
      setConfirmRevoke(null)
    }
  }

  const handleExportCSV = () => {
    if (!data?.users || data.users.length === 0) {
      toast.error('No users to export')
      return
    }

    const headers = ['User ID', 'Email', 'Full Name', 'Earned At']
    const rows = data.users.map((ub: UserBadge) => [
      ub.user_id,
      ub.profiles?.email || '',
      ub.profiles?.full_name || '',
      new Date(ub.unlocked_at).toISOString(),
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `badge-users-${badgeId}-${Date.now()}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    toast.success('CSV exported successfully')
  }

  if (isLoading) {
    return (
      <AdminCard accentColor="info">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </AdminCard>
    )
  }

  if (error) {
    return (
      <AdminCard accentColor="warning">
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground">
            Failed to load users: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      </AdminCard>
    )
  }

  if (!data?.users || data.users.length === 0) {
    return (
      <AdminCard accentColor="info">
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground">No users have earned this badge yet</p>
        </div>
      </AdminCard>
    )
  }

  const columns: ResponsiveTableColumn<UserBadge>[] = [
    {
      key: 'user',
      header: 'User',
      render: (ub) => (
        <div>
          <div className="font-medium">{ub.profiles?.full_name || 'Unknown'}</div>
          <div className="text-xs text-muted-foreground">{ub.profiles?.email || ub.user_id}</div>
        </div>
      ),
    },
    {
      key: 'user_id',
      header: 'User ID',
      render: (ub) => (
        <code className="text-xs bg-muted px-2 py-1 rounded">{ub.user_id.slice(0, 8)}...</code>
      ),
    },
    {
      key: 'earned_at',
      header: 'Earned At',
      render: (ub) => (
        <div className="text-sm">
          {formatDistanceToNow(new Date(ub.unlocked_at), { addSuffix: true })}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (ub) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setConfirmRevoke(ub.id)}
          disabled={revokingId === ub.id}
          className="text-destructive hover:text-destructive"
        >
          {revokingId === ub.id ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Trash2 className="h-4 w-4 mr-1" />
              <span className="hidden md:inline">Revoke</span>
            </>
          )}
        </Button>
      ),
    },
  ]

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {data.pagination.total} user{data.pagination.total !== 1 ? 's' : ''} earned this badge
          </div>
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        <ResponsiveTable
          columns={columns}
          data={data.users}
          emptyMessage="No users found"
        />

        {/* Pagination */}
        {data.pagination.total_pages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {((page - 1) * 50) + 1} to {Math.min(page * 50, data.pagination.total)} of {data.pagination.total} users
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <div className="text-sm text-muted-foreground">
                Page {page} of {data.pagination.total_pages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(data.pagination.total_pages, p + 1))}
                disabled={page >= data.pagination.total_pages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Revoke Confirmation Dialog */}
      <AlertDialog open={!!confirmRevoke} onOpenChange={(open) => !open && setConfirmRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Badge</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke this badge from the user? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmRevoke && handleRevoke(confirmRevoke)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

